import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect } from "react";
import { CASE_STUDIES } from "@/data/caseStudies";

/**
 * /case-studies index page.
 *
 * Lists every case study in the registry. Injects an ItemList schema
 * so LLMs treating the URL as a collection page get a clean summary
 * of the entire case-study library.
 */
export default function CaseStudies() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Case studies — Facetra";

    const canonicalUrl = "https://facetra.studio/case-studies";
    const itemList = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": canonicalUrl,
      name: "Facetra case studies",
      description:
        "Real-world case studies from Indian ateliers and independent designers who use Facetra to shorten sketch-to-CAD cycles.",
      inLanguage: "en-IN",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: CASE_STUDIES.map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `https://facetra.studio/case-studies/${c.slug}`,
          name: c.title,
        })),
      },
    };

    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.dataset.page = "case-studies";
    s.text = JSON.stringify(itemList);
    document.head.appendChild(s);

    const link = document.createElement("link");
    link.rel = "canonical";
    link.dataset.page = "case-studies";
    link.href = canonicalUrl;
    document.head.appendChild(link);

    const desc = document.createElement("meta");
    desc.name = "description";
    desc.dataset.page = "case-studies";
    desc.content =
      "Facetra case studies — how Indian ateliers cut sketch-to-CAD time and grew designs-per-week using AI-assisted jewellery design.";
    document.head.appendChild(desc);

    return () => {
      document.title = prevTitle;
      s.remove();
      link.remove();
      desc.remove();
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-[#0B0B0B] text-white relative overflow-hidden"
      data-testid="page-case-studies"
    >
      <div className="absolute inset-0 radial-gold-glow opacity-30 pointer-events-none" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      <header className="relative border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="case-studies-home-link">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#F3CE5A] to-[#8B6914] flex items-center justify-center shadow-[0_0_18px_rgba(212,175,55,0.35)]">
              <span className="font-display text-black text-sm font-bold">F</span>
            </div>
            <span className="font-display text-lg tracking-tight">Facetra</span>
          </Link>
          <Link
            to="/"
            data-testid="case-studies-back-link"
            className="text-xs text-white/55 hover:text-[#D4AF37] inline-flex items-center gap-1.5 transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Home
          </Link>
        </div>
      </header>

      <main className="relative max-w-4xl mx-auto px-6 lg:px-10 py-14">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-4">
          Case studies
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.08]">
          How real ateliers use Facetra
        </h1>
        <p className="mt-4 text-white/60 text-[15px] max-w-2xl">
          Short, honest, numbers-forward write-ups of how designers and ateliers
          shortened their sketch-to-CAD cycle, freed up karigar time, and shipped
          more designs per bridal season with Facetra.
        </p>

        {CASE_STUDIES.length === 0 ? (
          <div className="mt-12 rounded-2xl border border-white/8 bg-[#0E0E0E] p-8 text-center text-white/50 text-sm">
            First case studies publishing soon.
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            {CASE_STUDIES.map((c) => (
              <Link
                key={c.slug}
                to={`/case-studies/${c.slug}`}
                data-testid={`case-study-card-${c.slug}`}
                className="group rounded-2xl border border-white/8 bg-[#0E0E0E] p-6 hover:border-[#D4AF37]/30 transition flex flex-col"
              >
                <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/40 mb-2">
                  {c.discipline} · {c.location}
                </div>
                <div className="text-[16px] text-white font-medium leading-snug group-hover:text-[#D4AF37] transition">
                  {c.title}
                </div>
                <p className="mt-3 text-[13px] text-white/55 leading-relaxed line-clamp-3">
                  {c.tldr}
                </p>
                {Array.isArray(c.metrics) && c.metrics.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {c.metrics.slice(0, 2).map((m) => (
                      <span
                        key={m.label}
                        className="text-[10px] uppercase tracking-[0.15em] text-[#F3CE5A] border border-[#D4AF37]/30 rounded-full px-2.5 py-1"
                      >
                        {m.label}: {m.value}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-5 text-[11px] text-white/40 inline-flex items-center gap-1">
                  Read case study <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-14 pt-8 border-t border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-white/40">
          <span>© {new Date().getFullYear()} Facetra Design Studio Private Limited</span>
          <div className="flex items-center gap-5">
            <Link to="/how-it-works" className="hover:text-white/70 transition">How it works</Link>
            <Link to="/glossary" className="hover:text-white/70 transition">Glossary</Link>
            <Link to="/articles" className="hover:text-white/70 transition">Articles</Link>
            <Link to="/contact" className="hover:text-white/70 transition">Contact</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
